import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../hooks/use-auth";
import { Button } from "../components/ui/button";
import {
  User, Medal, FileText, Clock, Trophy, Activity, ArrowRight, LogOut, RefreshCcw,
  CreditCard, Banknote, IdCard, X, AlertTriangle,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function formatMoney(v) {
  if (!v) return "—";
  try { return `${parseFloat(v).toFixed(2)}€`; } catch { return v; }
}

function formatDate(d) {
  if (!d) return "—";
  try { return new Date(d + "T00:00:00").toLocaleDateString("pt-PT"); } catch { return d; }
}

export default function DashboardPage() {
  const { token, athlete: authAthlete, logout, refreshProfile } = useAuth();
  const [athlete, setAthlete] = useState(null);
  const [membership, setMembership] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [showCard, setShowCard] = useState(false);
  const [showInvoices, setShowInvoices] = useState(false);
  const [invoiceYear, setInvoiceYear] = useState(new Date().getFullYear().toString());

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [profileRes, membershipRes, invoicesRes] = await Promise.allSettled([
          axios.get(`${API}/athletes/me`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/athletes/me/membership`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/athletes/me/invoices`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        if (profileRes.status === "fulfilled") setAthlete(profileRes.value.data);
        if (membershipRes.status === "fulfilled") setMembership(membershipRes.value.data);
        if (invoicesRes.status === "fulfilled") setInvoices(invoicesRes.value.data || []);
      } catch {
        setAthlete(null);
      } finally {
        setFetching(false);
      }
    };
    fetchAll();
  }, [token]);

  // Years available from invoices
  const invoiceYears = [...new Set(invoices.map(inv => {
    if (!inv.data_emissao) return null;
    try { return new Date(inv.data_emissao + "T00:00:00").getFullYear().toString(); } catch { return null; }
  }).filter(Boolean))].sort((a, b) => b - a);

  // Garante que o ano selecionado existe nas faturas. Ex: fatura pendente de
  // 2025 quando o ano atual é 2026 — por omissão mostra o ano mais recente com faturas.
  useEffect(() => {
    if (invoiceYears.length && !invoiceYears.includes(invoiceYear)) {
      setInvoiceYear(invoiceYears[0]);
    }
  }, [invoiceYears, invoiceYear]);

  if (fetching) {
    return (
      <main className="pt-32 pb-20 bg-stone-50 min-h-screen">
        <div className="max-w-4xl mx-auto px-6 flex items-center justify-center min-h-[40vh]">
          <div className="w-10 h-10 border-4 border-brand-200 border-t-brand-700 rounded-full animate-spin" />
        </div>
      </main>
    );
  }

  if (!athlete) {
    return (
      <main className="pt-32 pb-20 bg-stone-50 min-h-screen">
        <div className="max-w-4xl mx-auto px-6 text-center py-20">
          <p className="text-stone-500">Erro ao carregar perfil.</p>
          <Button onClick={logout} variant="outline" className="mt-4">Sair</Button>
        </div>
      </main>
    );
  }

  const pbCount = athlete.personal_bests?.length || 0;
  const mem = membership || {};
  const vencidasCount = invoices.filter(inv => inv.estado === "vencida").length;
  const pendentesCount = invoices.filter(inv => inv.estado === "pendente").length;

  // Faturas que bloqueiam a reinscricao (mesma regra do backend):
  // vencidas bloqueiam sempre; pendentes so quando emitidas ha mais de 60 dias.
  const SIXTY_DAYS_MS = 60 * 24 * 60 * 60 * 1000;
  const blockingCount = invoices.filter(inv => {
    if (inv.estado === "vencida") return true;
    if (inv.estado !== "pendente") return false;
    const d = new Date((inv.data_emissao || "") + "T00:00:00");
    return !isNaN(d) && (Date.now() - d.getTime()) > SIXTY_DAYS_MS;
  }).length;

  const filteredInvoices = invoices.filter(inv => {
    if (!inv.data_emissao) return true;
    try { return new Date(inv.data_emissao + "T00:00:00").getFullYear().toString() === invoiceYear; } catch { return true; }
  });

  return (
    <main className="pt-32 pb-20 bg-stone-50 min-h-screen">
      <div className="max-w-4xl mx-auto px-6">

        {/* ── Header / Cartão de Sócio ─────────────────────────── */}
        <div className="bg-white rounded-2xl shadow-xl border border-stone-100 p-8 mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-brand-600 to-brand-800 flex items-center justify-center text-white text-2xl font-bold shrink-0">
                {athlete.name.charAt(0).toUpperCase()}
              </div>
              <div>
                <h1 className="text-2xl font-bold text-stone-900">Olá, {athlete.name.split(" ")[0]}!</h1>
                <div className="flex flex-wrap gap-2 mt-1">
                  <span className="text-xs bg-brand-50 text-brand-700 px-2 py-0.5 rounded-full font-medium">
                    Sócio {athlete.member_number}
                  </span>
                  {athlete.modality && (
                    <span className="text-xs bg-ember-50 text-ember-700 px-2 py-0.5 rounded-full font-medium">
                      {athlete.modality}
                    </span>
                  )}
                  {athlete.escalao && (
                    <span className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded-full font-medium">
                      {athlete.escalao}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => setShowCard(true)} variant="ghost" size="sm" className="text-brand-700">
                <IdCard className="w-5 h-5" />
              </Button>
              <Button onClick={() => { refreshProfile(token); setFetching(true); setTimeout(() => setFetching(false), 500); }}
                variant="ghost" size="sm" className="text-stone-500">
                <RefreshCcw className="w-4 h-4" />
              </Button>
              <Button onClick={logout} variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50">
                <LogOut className="w-4 h-4 mr-1" /> Sair
              </Button>
            </div>
          </div>
        </div>

        {/* ── Quick Stats ──────────────────────────────────────── */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
          <StatCard icon={<Trophy className="w-5 h-5 text-ember-500" />} value={pbCount} label="Recordes" />
          <StatCard icon={<Activity className="w-5 h-5 text-green-600" />} value={athlete.status === "active" ? "Ativo" : "—"} label="Estado" />
          <StatCard icon={<Clock className="w-5 h-5 text-stone-400" />} value={athlete.age} label="Idade" />
        </div>

        {/* ── Quotas / Mensalidade ─────────────────────────────── */}
        {mem.data_prox_mensalidade && (
          <div className="bg-white rounded-2xl shadow-xl border border-stone-100 p-6 mb-6">
            <h2 className="text-lg font-bold text-stone-900 mb-4 flex items-center gap-2">
              <Banknote className="w-5 h-5 text-brand-600" /> Quotas e Mensalidades
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <Info label="Próx. Mensalidade" value={formatDate(mem.data_prox_mensalidade)} />
              <Info label="Valor Quota" value={formatMoney(mem.valor_quota)} />
              <Info label="Mensalidade" value={formatMoney(mem.valor_mensalidade)} />
              <Info label="Tipo Quota" value={mem.tipo_quota || "—"} />
              <Info label="Data Inscrição" value={formatDate(mem.data_inscricao)} />
              <Info label="Renovação Quota" value={formatDate(mem.data_renovacao_quota)} />
              <Info label="Desconto" value={mem.perc_desconto ? `${mem.perc_desconto}%` : "—"} />
              <Info label="Opção Pagamento" value={mem.opcao_pagamento || "—"} />
            </div>
          </div>
        )}

        {/* ── Alerta de Reinscrição ────────────────────────────── */}
        {athlete.is_atleta && athlete.is_reinscrito === false && blockingCount > 0 && (
          <div className="bg-red-50 border border-red-300 rounded-2xl p-6 mb-6 flex items-start gap-4">
            <AlertTriangle className="w-8 h-8 text-red-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-lg font-bold text-red-900 mb-1">Faturas por liquidar</h3>
              <p className="text-red-800 text-sm">
                Tens {blockingCount} fatura{blockingCount !== 1 ? "s" : ""} em aberto. Regulariza os pagamentos antes de renovares a inscrição.
              </p>
            </div>
          </div>
        )}
        {athlete.is_atleta && athlete.is_reinscrito === false && blockingCount === 0 && (
          <div className="bg-amber-50 border border-amber-300 rounded-2xl p-6 mb-6 flex items-start gap-4">
            <AlertTriangle className="w-8 h-8 text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-lg font-bold text-amber-900 mb-1">Reinscrição pendente</h3>
              <p className="text-amber-800 text-sm mb-4">
                Ainda não renovaste a tua inscrição para a época atual. Clica no botão abaixo para iniciares o processo.
              </p>
              <Link to="/area-socio/renovar"
                className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white px-5 py-2.5 rounded-xl font-medium transition-colors shadow-md">
                Renovar Inscrição <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        )}

        {/* ── Cards Row: Ficha Pessoal + Faturas ──────────────── */}
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <NavCard to="/area-socio/perfil" icon={<User className="w-6 h-6" />} color="brand"
            title="Ficha Pessoal" desc="Dados pessoais, contacto e encarregado de educação." />
          <div
            onClick={() => setShowInvoices(!showInvoices)}
            className="group bg-white rounded-xl border border-stone-100 p-6 hover:shadow-lg hover:border-brand-200 transition-all cursor-pointer"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-brand-50 group-hover:bg-brand-100 text-brand-700 transition-colors">
                  <FileText className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-bold text-stone-900">Faturas</h3>
                  <p className="text-sm">
                    {invoices.length === 0 ? (
                      <span className="text-stone-500">Nenhuma fatura disponível</span>
                    ) : (
                      <>
                        <span className="text-stone-500">{invoices.length} fatura{invoices.length !== 1 ? "s" : ""}</span>
                        {pendentesCount > 0 && (
                          <span className="text-amber-600 font-medium"> · {pendentesCount} pendente{pendentesCount !== 1 ? "s" : ""}</span>
                        )}
                        {vencidasCount > 0 && (
                          <span className="text-red-600 font-medium"> · {vencidasCount} vencida{vencidasCount !== 1 ? "s" : ""}</span>
                        )}
                      </>
                    )}
                  </p>
                </div>
              </div>
              <ArrowRight className={`w-5 h-5 text-stone-400 transition-transform ${showInvoices ? "rotate-90" : ""}`} />
            </div>
          </div>
        </div>

        {/* ── Faturas Panel ────────────────────────────────────── */}
        {showInvoices && (
          <div className="bg-white rounded-2xl shadow-xl border border-stone-100 p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-stone-900 flex items-center gap-2">
                <FileText className="w-5 h-5 text-brand-600" /> Faturas
              </h2>
              {invoiceYears.length > 1 && (
                <select
                  value={invoiceYear}
                  onChange={e => setInvoiceYear(e.target.value)}
                  className="text-sm border border-stone-200 rounded-lg px-3 py-1.5 bg-white text-stone-700 focus:outline-none focus:ring-2 focus:ring-brand-300"
                >
                  {invoiceYears.map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              )}
            </div>
            {filteredInvoices.length === 0 ? (
              <p className="text-stone-500 text-sm py-6 text-center">Nenhuma fatura encontrada para {invoiceYear}.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-stone-500 border-b">
                      <th className="pb-2 font-medium">Descrição</th>
                      <th className="pb-2 font-medium">Data</th>
                      <th className="pb-2 font-medium">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInvoices.map((inv, i) => (
                      <tr key={inv.id || i} className="border-b border-stone-50">
                        <td className="py-3 pr-2">
                          {inv.file_url ? (
                            <a href={inv.file_url} target="_blank" rel="noreferrer" className="text-brand-700 hover:underline">
                              {inv.descricao || inv.tipo || "Documento"}
                            </a>
                          ) : (inv.descricao || inv.tipo || "—")}
                        </td>
                        <td className="py-3 pr-2 text-stone-500">{formatDate(inv.data_emissao)}</td>
                        <td className="py-3">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            inv.estado === "paga" ? "bg-green-50 text-green-700" : inv.estado === "vencida" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"
                          }`}>
                            {inv.estado || "pendente"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── Cartão de Sócio Modal ───────────────────────────── */}
        {showCard && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowCard(false)}>
            <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-bold text-stone-900">Cartão de Sócio</h3>
                <button onClick={() => setShowCard(false)} className="text-stone-400 hover:text-stone-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="relative w-full aspect-[1.6/1] rounded-xl overflow-hidden shadow-lg">
                <img
                  src="/catv/template_cartao.jpeg"
                  alt="Template Cartão de Sócio"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                {/* ── Dados sobrepostos ────────────────────────── */}
                <div className="absolute inset-0 p-[8%] flex flex-col justify-center">
                  <div className="space-y-[5%] ml-[5%] text-white" style={{ marginTop: '15%' }}>
                    <div className="text-[clamp(11px,2.6vw,17px)] font-bold">{athlete.name || "—"}</div>
                    <div className="text-[clamp(11px,2.6vw,17px)] font-bold">Nº {athlete.member_number || "—"}</div>
                    <div className="text-[clamp(9px,2.2vw,14px)]">
                      Válido até {athlete.data_renovacao_quota ? formatDate(athlete.data_renovacao_quota) : "31/08/2026"}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </main>
  );
}

function StatCard({ icon, value, label }) {
  return (
    <div className="bg-white rounded-xl border border-stone-100 p-4 text-center">
      <div className="flex justify-center mb-2">{icon}</div>
      <p className="text-xl font-bold text-stone-900">{value}</p>
      <p className="text-xs text-stone-500">{label}</p>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="text-xs text-stone-500">{label}</p>
      <p className="font-medium text-stone-800">{value}</p>
    </div>
  );
}

function NavCard({ to, icon, color, title, desc }) {
  const colors = {
    brand: "bg-brand-50 group-hover:bg-brand-100 text-brand-700 border-brand-200",
    ember: "bg-ember-50 group-hover:bg-ember-100 text-ember-600 border-ember-200",
    stone: "bg-stone-100 group-hover:bg-stone-200 text-stone-600",
  };
  return (
    <Link to={to} className={`group bg-white rounded-xl border border-stone-100 p-6 hover:shadow-lg transition-all ${color === "brand" ? "hover:border-brand-200" : color === "ember" ? "hover:border-ember-200" : ""}`}>
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-colors ${colors[color] || colors.brand}`}>
        {icon}
      </div>
      <h3 className="font-bold text-stone-900 mb-1">{title}</h3>
      <p className="text-sm text-stone-500 mb-3">{desc}</p>
      <span className="text-sm font-semibold inline-flex items-center gap-1"
        style={{ color: color === "ember" ? "#E55B3C" : "#2D2A8F" }}>
        Ver mais <ArrowRight className="w-3.5 h-3.5" />
      </span>
    </Link>
  );
}
